import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { loadGsap } from '../../lib/loadGsap'
import './PaintedHeadline.css'

/* The painted headline (founder, 9 Oct 2026): info, error and sign-in result pages say their one
   sentence, huge, instead of showing a picture beside it. Solid brand colour, no paint (founder,
   14 Oct 2026). The motion is the landing's old hero word's: each letter falls in, squashes on the
   line, rebounds once and settles. Joined scripts (Arabic) fall a whole word at a time so the letters
   stay joined. It plays once, then rests. Reduced motion, or fonts too slow: the still sentence. */

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
const JOINED = /[؀-ۿݐ-ݿࢠ-ࣿ]/

function pieces(text: string) {
    const graphemes = (word: string) => typeof Intl !== 'undefined' && 'Segmenter' in Intl
        ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(word)].map(s => s.segment)
        : Array.from(word)
    return text.split(/\s+/).filter(Boolean).map(word => JOINED.test(word) ? [word] : graphemes(word))
}

export default function PaintedHeadline({ text, id, size = 'page', as: Tag = 'h1' }: {
    text: string
    id?: string
    /** page: the page's picture. inline: inside an app screen, smaller. hero: a word inside a larger
        headline, at the headline's own size (no fitting). */
    size?: 'page' | 'inline' | 'hero'
    as?: 'h1' | 'h2' | 'span'
}) {
    const ref = useRef<HTMLElement>(null)
    const words = useMemo(() => pieces(text), [text])
    // Hidden until the fall starts, so the still sentence never flashes first.
    const [waiting, setWaiting] = useState(() => !reducedMotion())
    const animate = useRef(waiting)

    // Always one line (founder, 9 Oct 2026): when the words are wider than the space, the size shrinks to fit.
    useLayoutEffect(() => {
        const h = ref.current
        if (!h || size === 'hero') return
        const fit = () => {
            h.style.fontSize = ''
            const room = h.clientWidth, need = h.scrollWidth
            if (need > room && room > 0) h.style.fontSize = `${parseFloat(getComputedStyle(h).fontSize) * (room / need) * 0.98}px`
        }
        fit()
        // The web font is wider than the fallback: measure again once it has arrived.
        let live = true
        document.fonts?.ready.then(() => { if (live) fit() })
        const observer = new ResizeObserver(fit)
        if (h.parentElement) observer.observe(h.parentElement)
        return () => { live = false; observer.disconnect() }
    }, [text, size])

    useLayoutEffect(() => {
        const h = ref.current
        if (!h || !animate.current) return
        let ctx: gsap.Context | undefined
        let live = true
        // Slow fonts or a slow script never hide the sentence for long: it shows still.
        const failSafe = window.setTimeout(() => { if (live && animate.current) { animate.current = false; setWaiting(false) } }, 1500)
        Promise.all([loadGsap(), document.fonts?.ready]).then(([g]) => {
            if (!live || !animate.current) return
            window.clearTimeout(failSafe)
            ctx = g.context(() => {
                const fs = parseFloat(getComputedStyle(h).fontSize)
                const em = (v: number) => v * fs
                const letters = [...h.querySelectorAll<HTMLElement>('.painted-headline__ch')]
                const tl = g.timeline({ delay: 0.12, onStart: () => setWaiting(false) })
                let t = 0
                letters.forEach((e, i) => {
                    const whole = e.dataset.whole === '1', sq = whole ? 0.5 : 1
                    if (i > 0) t += e.dataset.first === '1' ? 0.07 : 0.028
                    // A short drop, so a second-line letter never crosses the line above it.
                    const lift = 0.24 + ((i * 37) % 11) / 110
                    g.set(e, { transformOrigin: '50% 82%' })
                    tl.fromTo(e, { y: -em(lift), scaleX: 1, scaleY: 1 }, { y: 0, scaleX: 1 - 0.05 * sq, scaleY: 1 + 0.07 * sq, duration: 0.16, ease: 'power1.in' }, t)
                    tl.fromTo(e, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.05, ease: 'none' }, t)
                    tl.to(e, { scaleX: 1 + 0.1 * sq, scaleY: 1 - 0.14 * sq, duration: 0.035, ease: 'power2.out' }, t + 0.16)
                    tl.to(e, { y: -em(0.05), scaleX: 1 - 0.02 * sq, scaleY: 1 + 0.03 * sq, duration: 0.075, ease: 'power2.out' }, t + 0.195)
                    tl.to(e, { y: 0, scaleX: 1 + 0.025 * sq, scaleY: 1 - 0.025 * sq, duration: 0.07, ease: 'power2.in' }, t + 0.27)
                    tl.to(e, { scaleX: 1, scaleY: 1, duration: 0.08, ease: 'power2.out', clearProps: 'transform' }, t + 0.34)
                })
            }, h)
        }, () => setWaiting(false))
        return () => { live = false; window.clearTimeout(failSafe); ctx?.revert() }
    }, [words])

    // h1, h2 or span; typed as one element so the ref fits all three.
    const Element = Tag as 'h1'
    return <Element ref={ref as React.RefObject<HTMLHeadingElement>} id={id} className={`painted-headline painted-headline--${size}`} data-waiting={waiting || undefined}
        aria-label={text}>
        {words.map((word, w) => <Fragment key={w}>{w > 0 ? ' ' : null}<span className="painted-headline__word" aria-hidden="true">
            {word.map((ch, c) => <span key={c} className="painted-headline__ch" data-first={c === 0 ? '1' : undefined} data-whole={word.length === 1 && ch.length > 1 ? '1' : undefined}>{ch}</span>)}
        </span></Fragment>)}
    </Element>
}
